/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, TrendingUp, Layers } from 'lucide-react';
import ConfidenceChip from '@/components/terra/ConfidenceChip';

const BAND_TONE = {
  STRONG:   'text-emerald-700 bg-emerald-50 border-emerald-200',
  EMERGING: 'text-amber-700   bg-amber-50   border-amber-200',
  WEAK:     'text-stone-600   bg-stone-50   border-stone-200',
  UNKNOWN:  'text-stone-500   bg-stone-50   border-stone-200',
};

export default function RssIntelligencePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(180);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/terra-growth/intelligence/rss-dashboard?days=${days}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="rss-intelligence-page" className="max-w-7xl mx-auto px-6 py-8">
        <header className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239]">Terra Growth OS™ · Intelligence</p>
            <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">RSS Intelligence Dashboard</h1>
            <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-1 max-w-2xl">
              Right-Subscriber Score™ over time — band drift, dimension outliers, per-counselor
              quality distribution, and the ecosystem × RSS heatmap.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[90, 180, 365].map(d => (
              <button key={d} data-testid={`rss-days-${d}`} onClick={() => setDays(d)}
                      className={`text-[11px] rounded-full px-3 py-1 border ${
                        days === d ? 'bg-[#4A5D4E] text-white border-[#4A5D4E]' : 'bg-white text-[#5C5955] border-[#D8D4CE]'}`}>{d}d</button>
            ))}
          </div>
        </header>

        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Computing…
          </div>
        ) : !data || data.score_count === 0 ? (
          <div data-testid="rss-intelligence-empty" className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            No RSS scores in this window. Score leads to populate this view.
          </div>
        ) : (
          <div className="space-y-6">
            <BandDrift data={data.band_drift} />
            <Heatmap data={data.heatmap} />
            <PerCounselor data={data.per_counselor} />
            <Outliers data={data.dimension_outliers} />
          </div>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function BandDrift({ data }) {
  if (!data || data.length === 0) return null;
  const months = data.map(d => d.month);
  const maxTotal = Math.max(...data.map(d => d.total));
  return (
    <section data-testid="rss-band-drift" className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
      <header className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-3.5 h-3.5 text-[#4A5D4E]" />
        <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">Band drift over time</p>
      </header>
      <div className="flex items-end gap-2 h-40">
        {data.map(d => (
          <div key={d.month} className="flex flex-col items-center gap-1 flex-1 min-w-[60px]">
            <StackedBar d={d} max={maxTotal} />
            <span className="text-[9px] tabular-nums text-[#8A8681]">{d.month.slice(2)}</span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-[#8A8681] mt-2">{months.length} months · {data.reduce((s, d) => s + d.total, 0)} scores</p>
    </section>
  );
}
function StackedBar({ d, max }) {
  const total = d.total || 1;
  const heightPct = (total / max) * 100;
  const bands = ['STRONG', 'EMERGING', 'WEAK'];
  return (
    <div className="w-full bg-[#F4F2EE] rounded relative" style={{ height: `${heightPct}%`, minHeight: '4px' }}>
      <div className="absolute inset-0 flex flex-col rounded overflow-hidden">
        {bands.map(b => {
          const v = d[b] || 0;
          if (!v) return null;
          const pct = (v / total) * 100;
          const color = b === 'STRONG' ? '#10B981' : b === 'EMERGING' ? '#F59E0B' : '#A8A29E';
          return <div key={b} style={{ height: `${pct}%`, background: color }} />;
        })}
      </div>
    </div>
  );
}

function Heatmap({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <section data-testid="rss-heatmap" className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
      <header className="flex items-center gap-2 mb-3">
        <Layers className="w-3.5 h-3.5 text-[#8C6239]" />
        <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">Ecosystem × RSS band</p>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-[0.12em] text-[#8A8681]">
            <tr>
              <th className="px-3 py-2 text-left">Ecosystem</th>
              <th className="px-3 py-2 text-right">Strong</th>
              <th className="px-3 py-2 text-right">Emerging</th>
              <th className="px-3 py-2 text-right">Weak</th>
              <th className="px-3 py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.map(r => (
              <tr key={r.ecosystem_code} className="border-t border-[#EBE8E3]"
                  data-testid={`heatmap-row-${r.ecosystem_code}`}>
                <td className="px-3 py-2 text-[#2C2A29]">{r.ecosystem_code}</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{r.STRONG || 0}</td>
                <td className="px-3 py-2 text-right tabular-nums text-amber-700">{r.EMERGING || 0}</td>
                <td className="px-3 py-2 text-right tabular-nums text-stone-600">{r.WEAK || 0}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PerCounselor({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <section data-testid="rss-per-counselor" className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681] mb-3">Per-counselor quality</p>
      <ul className="space-y-2">
        {data.map(c => (
          <li key={c.user_id} className="flex items-center justify-between gap-2"
              data-testid={`rss-counselor-${c.user_id}`}>
            <div className="min-w-0">
              <p className="text-sm text-[#2C2A29] truncate">{c.name || c.user_id}</p>
              <p className="text-[10px] text-[#8A8681]">{c.sample_count} scores · avg {c.avg_composite}</p>
            </div>
            <div className="flex gap-1">
              {Object.entries(c.by_band || {}).map(([b, n]) => (
                <span key={b}
                      className={`text-[10px] uppercase tracking-[0.14em] rounded-full border px-2 py-0.5 ${BAND_TONE[b] || BAND_TONE.UNKNOWN}`}>
                  {b}: {n}
                </span>
              ))}
              <ConfidenceChip code={c.confidence} sample={c.sample_count} compact />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Outliers({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <section data-testid="rss-outliers" className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681] mb-3">Dimension outliers</p>
      <ul className="space-y-2">
        {data.map(o => (
          <li key={o.lead_id} data-testid={`rss-outlier-${o.lead_id}`}
              className="rounded-xl border border-[#EBE8E3] bg-[#FBFaf9] p-3 flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[12px] text-[#2C2A29] truncate">{o.lead_id}</p>
              <p className="text-[10px] text-[#8A8681]">composite {o.composite} · {o.band}</p>
            </div>
            <div className="flex flex-wrap gap-1 max-w-[60%]">
              {o.low_dimensions.map(d => (
                <span key={d.dimension}
                      className="text-[9px] uppercase tracking-[0.12em] rounded border border-stone-200 bg-stone-50 text-stone-600 px-1.5 py-0.5">
                  {d.dimension}: {d.value} · μ {d.centre_mean}
                </span>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
